import { fetchRequestMiddleware, fetchResponseMiddleware } from './middleware'
import { CashApiRequest, CashRequestOptions } from './middleware/fetch/types'

export type SerializeQueryParameters = { [index: string]: string | string[] }

export const serializeQueryParams = (parameters: SerializeQueryParameters): string =>
  Object.entries(parameters)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        const encodedParams = value.map((param: string) => encodeURIComponent(param))
        return `${encodeURIComponent(key)}=${encodedParams.join(`&${encodeURIComponent(key)}=`)}`
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    })
    .join('&')

export const createQueryString = (parameters: SerializeQueryParameters): string => {
  const queryParams = parameters && Object.keys(parameters).length ? serializeQueryParams(parameters) : null
  return queryParams ? `?${queryParams}` : ''
}

// Older versions of Edge / Chrome don't support the referrerPolicy option in `fetch`
// This is a basic feature detection function that will include it when supported
function supportsSameOriginReferrerPolicy() {
  try {
    new Request('_', {
      referrerPolicy: 'same-origin',
    })
  } catch (e) {
    return false
  }
  return true
}
const loggedFetch: typeof fetch = async (input, init) => {
  try {
    return await fetch(input, init)
  } catch (e) {
    const error = e as Error
    throw new Error(`Error fetching path '${input}' reason: ${error.message}`)
  }
}
const fetchReq = async <T>(
  path: string,
  request: CashApiRequest,
  { optionalMiddleware, ...options }: CashRequestOptions = {},
  data?: T
) => {
  const params = { ...request }
  if (supportsSameOriginReferrerPolicy()) {
    params.referrerPolicy = 'same-origin'
  }
  const response = await loggedFetch(
    path,
    await fetchRequestMiddleware.execute(
      {
        cache: 'no-cache',
        credentials: 'same-origin',
        redirect: 'error',
        signal: options.signal,
        ...params,
        data,
      },
      { options, path },
      optionalMiddleware?.request
    )
  )

  return fetchResponseMiddleware.execute(response, { request, options }, optionalMiddleware?.response)
}

export const post = <I, O>(path: string, data?: I, options?: CashRequestOptions): Promise<O> => {
  return fetchReq(
    path,
    {
      method: 'POST',
    },
    options,
    data
  )
}

export const get = <O>(path: string, params?: SerializeQueryParameters, options?: CashRequestOptions): Promise<O> =>
  fetchReq(
    `${path}${createQueryString(params || {})}`,
    {
      method: 'GET',
    },
    options
  )
